/**
 * AI Platform
 * Chat API of the hosted agent server (`@convercus/agent-server`). Read a chat as a view: a snapshot from `GET /chats/{chatId}/view`, then its changes from the NDJSON stream `GET /chats/{chatId}/view/stream?after` or the long-poll `GET /chats/{chatId}/view?after&waitMs`. The service sits behind the API gateway, which validates the JWT and forwards identity as plain headers — `program`, `user-id`, `username` and the user\'s `roles`. The view stream is the exception: it is reached without the gateway and verifies the `Authorization: Bearer` token itself. With `AUTH_MODE=mock` (local dev) every request runs as a canned identity and the headers are ignored. Every route but health names the permission it needs and answers 403 to a role that grants none of them: `agent.bi` (ANALYTICS, SUPER_ADMIN) for agents and chats; `agent.config.read`/`agent.config.write` (PROGRAM_MANAGER, SUPER_ADMIN) for the settings; dedicated mode and the spend ledger are SUPER_ADMIN only.
 *
 * The version of the OpenAPI document: 1.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */

import { Inject, Injectable, Optional }                      from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams,
         HttpResponse, HttpEvent, HttpParameterCodec }       from '@angular/common/http';
import { CustomHttpParameterCodec }                          from '../encoder';
import { Observable }                                        from 'rxjs';

import { ModelCatalog } from '../model/models';
import { ModelSettings } from '../model/models';
import { ProgramBudget } from '../model/models';
import { ProgramSpendMonth } from '../model/models';
import { PutModelSettingsRequest } from '../model/models';

import { BASE_PATH, COLLECTION_FORMATS }                     from '../variables';
import { Configuration }                                     from '../configuration';
import {
    SettingsServiceInterface,
    GetProgramSpendRequestParams,
    PutModelSettingsRequestParams
} from './settings.serviceInterface';



@Injectable({
  providedIn: 'root'
})
export class SettingsService implements SettingsServiceInterface {

    protected basePath = 'http://localhost';
    public defaultHeaders = new HttpHeaders();
    public configuration = new Configuration();
    public encoder: HttpParameterCodec;

    constructor(protected httpClient: HttpClient, @Optional()@Inject(BASE_PATH) basePath: string, @Optional() configuration: Configuration) {
        if (configuration) {
            this.configuration = configuration;
        }
        if (typeof this.configuration.basePath !== 'string') {
            if (typeof basePath !== 'string') {
                basePath = this.basePath;
            }
            this.configuration.basePath = basePath;
        }
        this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }


    private addToHttpParams(httpParams: HttpParams, value: any, key?: string): HttpParams {
        if (typeof value === "object" && value instanceof Date === false) {
            httpParams = this.addToHttpParamsRecursive(httpParams, value);
        } else {
            httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
        }
        return httpParams;
    }

    private addToHttpParamsRecursive(httpParams: HttpParams, value?: any, key?: string): HttpParams {
        if (value == null) {
            return httpParams;
        }

        if (typeof value === "object") {
            if (Array.isArray(value)) {
                (value as any[]).forEach( elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
            } else if (value instanceof Date) {
                if (key != null) {
                    httpParams = httpParams.append(key,
                        (value as Date).toISOString().substr(0, 10));
                } else {
                   throw Error("key may not be null if value is Date");
                }
            } else {
                Object.keys(value).forEach( k => httpParams = this.addToHttpParamsRecursive(
                    httpParams, value[k], key != null ? `${key}.${k}` : k));
            }
        } else if (key != null) {
            httpParams = httpParams.append(key, value);
        } else {
            throw Error("key may not be null if value is not object or array");
        }
        return httpParams;
    }

    /**
     * Remove the program\&#39;s model settings
     * Needs &#x60;agent.config.write&#x60;. Deletes the override, every stored credential and any dedicated limit — the program reverts to the platform default in platform mode. A dedicated program can only be deleted by a caller with the dedicated permission (SUPER_ADMIN): its limit is ours, and a program manager leaves dedicated mode by saving byok or platform instead.
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public deleteModelSettings(observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined}): Observable<any>;
    public deleteModelSettings(observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined}): Observable<HttpResponse<any>>;
    public deleteModelSettings(observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined}): Observable<HttpEvent<any>>;
    public deleteModelSettings(observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: undefined}): Observable<any> {

        let headers = this.defaultHeaders;

        let credential: string | undefined;
        // authentication (program) required
        credential = this.configuration.lookupCredential('program');
        if (credential) {
            headers = headers.set('program', credential);
        }

        // authentication (user-id) required
        credential = this.configuration.lookupCredential('user-id');
        if (credential) {
            headers = headers.set('user-id', credential);
        }

        // authentication (username) required
        credential = this.configuration.lookupCredential('username');
        if (credential) {
            headers = headers.set('username', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.delete<any>(`${this.configuration.basePath}/ai-platform-service/settings/model`,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Get the program\&#39;s model settings
     * The platform default plus the program\&#39;s override, if configured: its mode, its model, one row per stored customer key (provider, owner, hint, verification status and custom endpoint) and, in dedicated mode, the monthly limit. Stored API keys are write-only — no response ever carries one. Dedicated mode stores no key; its &#x60;monthlyLimitUsd&#x60; is shown only to callers with the dedicated permission (SUPER_ADMIN), and every reader sees the same cap on &#x60;GET /settings/budget&#x60;. The &#x60;keyProvider&#x60;/&#x60;keyHint&#x60;/&#x60;keyStatus&#x60; fields are deprecated mirrors of the first credential, kept for one release.
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public getModelSettings(observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<ModelSettings>;
    public getModelSettings(observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<ModelSettings>>;
    public getModelSettings(observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<ModelSettings>>;
    public getModelSettings(observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {

        let headers = this.defaultHeaders;

        let credential: string | undefined;
        // authentication (program) required
        credential = this.configuration.lookupCredential('program');
        if (credential) {
            headers = headers.set('program', credential);
        }

        // authentication (user-id) required
        credential = this.configuration.lookupCredential('user-id');
        if (credential) {
            headers = headers.set('user-id', credential);
        }

        // authentication (username) required
        credential = this.configuration.lookupCredential('username');
        if (credential) {
            headers = headers.set('username', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.get<ModelSettings>(`${this.configuration.basePath}/ai-platform-service/settings/model`,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Get the program\&#39;s monthly AI budget
     * Open to the same callers as &#x60;GET /settings/model&#x60; — the budget bar is shown to whoever chats, not only to admins. Platform and dedicated programs get &#x60;budget&#x60;: this month\&#39;s billable spend, the limit it counts against, the percentage and the bar state, and whether reaching the limit actually blocks sends. A program on its own key gets no budget at all — only &#x60;billing.provider&#x60;, because its spend never reaches a Convercus invoice. &#x60;spentUsd&#x60; is the same stored number the send-time gate reads, so the bar and the block can never disagree. Every reader gets the whole object, dedicated programs included: the month\&#39;s usage is program-level information (DEV-532 QA).
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public getProgramBudget(observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<ProgramBudget>;
    public getProgramBudget(observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<ProgramBudget>>;
    public getProgramBudget(observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<ProgramBudget>>;
    public getProgramBudget(observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {

        let headers = this.defaultHeaders;

        let credential: string | undefined;
        // authentication (program) required
        credential = this.configuration.lookupCredential('program');
        if (credential) {
            headers = headers.set('program', credential);
        }

        // authentication (user-id) required
        credential = this.configuration.lookupCredential('user-id');
        if (credential) {
            headers = headers.set('user-id', credential);
        }

        // authentication (username) required
        credential = this.configuration.lookupCredential('username');
        if (credential) {
            headers = headers.set('username', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.get<ProgramBudget>(`${this.configuration.basePath}/ai-platform-service/settings/budget`,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Read the program\&#39;s monthly spend ledger
     * &#x60;agent.spend.read&#x60;, SUPER_ADMIN only — the raw internal cost view, newest month first. One document per UTC calendar month: &#x60;billable&#x60; is what Convercus pays for (every run whose key source is not byok) and is the number the budget bar and the budget gate read; &#x60;byok&#x60; is what ran on the program\&#39;s own key and is informational only. The ledger outlives the chats it was built from.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public getProgramSpend(requestParameters: GetProgramSpendRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<Array<ProgramSpendMonth>>;
    public getProgramSpend(requestParameters: GetProgramSpendRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<Array<ProgramSpendMonth>>>;
    public getProgramSpend(requestParameters: GetProgramSpendRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<Array<ProgramSpendMonth>>>;
    public getProgramSpend(requestParameters: GetProgramSpendRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {
        const months = requestParameters.months;

        let queryParameters = new HttpParams({encoder: this.encoder});
        if (months !== undefined && months !== null) {
          queryParameters = this.addToHttpParams(queryParameters,
            <any>months, 'months');
        }

        let headers = this.defaultHeaders;

        let credential: string | undefined;
        // authentication (program) required
        credential = this.configuration.lookupCredential('program');
        if (credential) {
            headers = headers.set('program', credential);
        }

        // authentication (user-id) required
        credential = this.configuration.lookupCredential('user-id');
        if (credential) {
            headers = headers.set('user-id', credential);
        }

        // authentication (username) required
        credential = this.configuration.lookupCredential('username');
        if (credential) {
            headers = headers.set('username', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.get<Array<ProgramSpendMonth>>(`${this.configuration.basePath}/ai-platform-service/settings/spend`,
            {
                params: queryParameters,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * List the model catalog
     * Two tiers in one list: \&quot;platform\&quot; models selectable in platform mode and in dedicated mode (Convercus\&#39;s models: Claude on Amazon Bedrock, processed in EU regions only), \&quot;byok\&quot; models selectable in byok mode on the program\&#39;s own key. The tiers may overlap — the same provider/id can appear once per tier. &#x60;defaultModel&#x60; is what runs when nothing is configured. A byok program can only use the entries of the providers it holds a key for, which &#x60;GET /settings/model&#x60; lists.
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public listModels(observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<ModelCatalog>;
    public listModels(observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<ModelCatalog>>;
    public listModels(observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<ModelCatalog>>;
    public listModels(observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {

        let headers = this.defaultHeaders;

        let credential: string | undefined;
        // authentication (program) required
        credential = this.configuration.lookupCredential('program');
        if (credential) {
            headers = headers.set('program', credential);
        }

        // authentication (user-id) required
        credential = this.configuration.lookupCredential('user-id');
        if (credential) {
            headers = headers.set('user-id', credential);
        }

        // authentication (username) required
        credential = this.configuration.lookupCredential('username');
        if (credential) {
            headers = headers.set('username', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.get<ModelCatalog>(`${this.configuration.basePath}/ai-platform-service/models`,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Set the program\&#39;s model (and optionally its API key)
     * Needs &#x60;agent.config.write&#x60; (PROGRAM_MANAGER, SUPER_ADMIN). Validates the model against the chosen mode\&#39;s tier — platform and dedicated both select from the platform tier, byok from the byok tier — and byok needs a key for the model\&#39;s provider, sent with this request or already stored. The mode, not the model, decides the key source. Dedicated is Convercus-managed with the program\&#39;s own monthly limit: it runs on the platform models and takes no key, so &#x60;credentials&#x60; or &#x60;apiKey&#x60; with it is a 422. Saving &#x60;mode: \&quot;dedicated\&quot;&#x60; or its monthly limit needs the dedicated permission, SUPER_ADMIN only; a program manager may switch a dedicated program to byok or platform, which drops the stored limit. Each provided key is verified with a live provider call against its endpoint, encrypted at rest, and stored write-only — it is never returned, and a request that fails any check writes nothing.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public putModelSettings(requestParameters: PutModelSettingsRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<ModelSettings>;
    public putModelSettings(requestParameters: PutModelSettingsRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<ModelSettings>>;
    public putModelSettings(requestParameters: PutModelSettingsRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<ModelSettings>>;
    public putModelSettings(requestParameters: PutModelSettingsRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {
        const putModelSettingsRequest = requestParameters.putModelSettingsRequest;
        if (putModelSettingsRequest === null || putModelSettingsRequest === undefined) {
            throw new Error('Required parameter putModelSettingsRequest was null or undefined when calling putModelSettings.');
        }

        let headers = this.defaultHeaders;

        let credential: string | undefined;
        // authentication (program) required
        credential = this.configuration.lookupCredential('program');
        if (credential) {
            headers = headers.set('program', credential);
        }

        // authentication (user-id) required
        credential = this.configuration.lookupCredential('user-id');
        if (credential) {
            headers = headers.set('user-id', credential);
        }

        // authentication (username) required
        credential = this.configuration.lookupCredential('username');
        if (credential) {
            headers = headers.set('username', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected !== undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.put<ModelSettings>(`${this.configuration.basePath}/ai-platform-service/settings/model`,
            putModelSettingsRequest,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

}
